import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useOrdersApiService } from "@/hooks/useOrdersApi";
import {
  canAccessFeature as evaluateFeatureAccess,
  FEATURE_ACCESS,
  type FeatureAccessContext,
  type FeatureId,
} from "@/lib/featureAccess";
import { Role } from "@/types/auth";

const DELEGATION_CACHE_KEY = "verification_delegation_access_cache";
const DELEGATION_CACHE_TTL = 5 * 60 * 1000;

interface DelegationCache {
  delegatedToUserId: string | null;
  timestamp: number;
}

function readDelegationCache(): DelegationCache | null {
  try {
    const cached = localStorage.getItem(DELEGATION_CACHE_KEY);
    if (!cached) return null;

    const parsed = JSON.parse(cached) as Partial<DelegationCache>;
    const hasValidUserId =
      typeof parsed.delegatedToUserId === "string" ||
      parsed.delegatedToUserId === null;
    if (
      hasValidUserId &&
      typeof parsed.timestamp === "number" &&
      Date.now() - parsed.timestamp < DELEGATION_CACHE_TTL
    ) {
      return parsed as DelegationCache;
    }

    localStorage.removeItem(DELEGATION_CACHE_KEY);
    return null;
  } catch {
    return null;
  }
}

function writeDelegationCache(delegatedToUserId: string | null) {
  try {
    const cache: DelegationCache = {
      delegatedToUserId,
      timestamp: Date.now(),
    };
    localStorage.setItem(DELEGATION_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Access decisions still work without local storage.
  }
}

export function useFeatureAccess(feature?: FeatureId) {
  const { role, user } = useAuth();
  const ordersApi = useOrdersApiService();
  const shouldLoadDelegation =
    (feature === undefined || feature === "verifications") &&
    role !== Role.ADMIN;
  const cachedDelegation = useMemo(
    () => (shouldLoadDelegation ? readDelegationCache() : null),
    [shouldLoadDelegation],
  );
  const hasCachedDelegationAccess =
    cachedDelegation?.delegatedToUserId === user?.id;
  const [delegatedToUserId, setDelegatedToUserId] = useState<string | null>(
    cachedDelegation?.delegatedToUserId ?? null,
  );
  const [loadingDelegation, setLoadingDelegation] = useState(
    shouldLoadDelegation && !hasCachedDelegationAccess,
  );

  useEffect(() => {
    if (!shouldLoadDelegation) {
      setLoadingDelegation(false);
      return;
    }

    let cancelled = false;
    setDelegatedToUserId(cachedDelegation?.delegatedToUserId ?? null);
    setLoadingDelegation(!hasCachedDelegationAccess);

    const loadDelegation = async () => {
      try {
        const result = await ordersApi.getVerificationDelegation();
        if (cancelled) return;

        const delegatedUserId = result.delegation?.delegated_to?.id ?? null;
        setDelegatedToUserId(delegatedUserId);
        writeDelegationCache(delegatedUserId);
      } catch {
        if (!cancelled && !cachedDelegation) {
          setDelegatedToUserId(null);
        }
      } finally {
        if (!cancelled) setLoadingDelegation(false);
      }
    };

    void loadDelegation();
    return () => {
      cancelled = true;
    };
  }, [
    cachedDelegation,
    hasCachedDelegationAccess,
    ordersApi,
    shouldLoadDelegation,
  ]);

  const context = useMemo<FeatureAccessContext>(
    () => ({
      role,
      isManager: Boolean(user?.is_manager),
      isVerificationDelegate: delegatedToUserId === user?.id,
    }),
    [delegatedToUserId, role, user?.id, user?.is_manager],
  );

  const canAccessFeature = useCallback(
    (featureToCheck: FeatureId) =>
      evaluateFeatureAccess(featureToCheck, context),
    [context],
  );

  const isAccessLoading = useCallback(
    (featureToCheck: FeatureId) =>
      featureToCheck === "verifications" &&
      FEATURE_ACCESS.verifications.enabled &&
      (!role || !FEATURE_ACCESS.verifications.roles[role]) &&
      loadingDelegation,
    [loadingDelegation, role],
  );

  return {
    allowed: feature ? canAccessFeature(feature) : false,
    loading: feature ? isAccessLoading(feature) : false,
    canAccessFeature,
    isAccessLoading,
  };
}
